/** Ler e pôr a câmara, para a sala de revisão.
 *
 *  Está aqui, e não no painel, porque é a única parte da sala que fala
 *  com o `camera-controls` do viewer, e assim o painel fica só com a
 *  interface.
 *
 *  Uma nota sobre o que NÃO se sincroniza: o zoom e a projecção
 *  (perspectiva ou ortogonal) ficam de fora. Quem acompanha pode estar
 *  num ecrã com outra forma, e forçar-lhe a projecção do apresentador
 *  dava-lhe uma vista pior do que a que tinha. Posição e alvo chegam
 *  para toda a gente estar a olhar para o mesmo sítio.
 */

import * as THREE from 'three'

import { viewerApi } from '@viewer/lib/viewerApi'
import type { EstadoCamara } from '@viewer/room/roomProtocol'

interface ControlosDaCamara {
  getPosition: (alvo: THREE.Vector3) => THREE.Vector3
  getTarget: (alvo: THREE.Vector3) => THREE.Vector3
  setLookAt: (
    px: number,
    py: number,
    pz: number,
    tx: number,
    ty: number,
    tz: number,
    suave?: boolean,
  ) => Promise<void>
}

function controlos(): ControlosDaCamara | null {
  const world = viewerApi.getWorld() as unknown as {
    camera?: { controls?: ControlosDaCamara }
  } | null
  return world?.camera?.controls ?? null
}

/** Onde está a câmara agora, ou null se ainda não houver viewer. */
export function lerCamara(): EstadoCamara | null {
  const c = controlos()
  if (!c) return null
  try {
    const p = c.getPosition(new THREE.Vector3())
    const a = c.getTarget(new THREE.Vector3())
    return { posicao: [p.x, p.y, p.z], alvo: [a.x, a.y, a.z] }
  } catch {
    return null
  }
}

/** Leva a câmara para onde o apresentador está a olhar.
 *
 *  Com transição suave: um salto seco a cada mensagem enjoa quem está
 *  a acompanhar, e a 15 mensagens por segundo a transição encadeia-se
 *  e dá um movimento contínuo. */
export async function porCamara(camara: EstadoCamara): Promise<void> {
  const c = controlos()
  if (!c) return
  try {
    await c.setLookAt(
      camara.posicao[0],
      camara.posicao[1],
      camara.posicao[2],
      camara.alvo[0],
      camara.alvo[1],
      camara.alvo[2],
      true,
    )
  } catch {
    // Uma câmara que o viewer recuse não pode partir a sala.
  }
}

/** Mudou o suficiente para valer a pena enviar?
 *
 *  Sem isto mandava-se a câmara 15 vezes por segundo mesmo com o rato
 *  parado. O limiar é relativo à distância ao alvo, senão um modelo em
 *  milímetros disparava a cada tremor e um modelo em quilómetros nunca
 *  disparava. */
export function mudouOSuficiente(
  antes: EstadoCamara | null,
  agora: EstadoCamara,
  fraccao = 0.001,
): boolean {
  if (!antes) return true
  const d = (a: readonly number[], b: readonly number[]) =>
    Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
  const escala = Math.max(1, d(agora.posicao, agora.alvo))
  const limiar = escala * fraccao
  return (
    d(antes.posicao, agora.posicao) > limiar ||
    d(antes.alvo, agora.alvo) > limiar
  )
}
